import { userFacingMessage } from '../../api/errors'
import { useProfile } from '../../api/queries/profile'
import { shellStyles } from '../../app/AppShell'
import { Button } from '../../ui/Button'
import { ErrorState, Skeleton } from '../../ui/Feedback'
import { DeleteAccountCard, ExportCard, PlanCard } from './AccountCards'
import { OpenSectionsProvider } from './SettingsCard'
import { AutoArchiveCard } from './AutoArchiveCard'
import { BillingDetailsCard, CallsCard, CompanyCard, PersonalCard } from './ProfileCards'
import styles from './Settings.module.css'

/**
 * Your account, your company, and the two things you can do to your data.
 *
 * The order is the old app's, which reads outward: who you are, where you work,
 * how your calls are handled, what you are entitled to, where invoices go, and
 * only then the two irreversible-ish operations.
 *
 * Auto-archive sits after Calls because it governs how calls-driven deals leave
 * the board. The old app's card offered 30/60/90/Never and persisted none of it
 * (`setArchDays` set a variable nothing ever read); this one saves, and a sweep
 * on the board read applies it (backend/app/domain/archive.py). Closes docs/14
 * gap (m).
 */
export function SettingsPage() {
  const profile = useProfile()

  return (
    <>
      <header className={shellStyles.topbar}>
        <h1 className={styles.pageTitle}>Settings</h1>
      </header>

      <main className={shellStyles.content}>
        {profile.isPending ? (
          <div className={styles.page}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height={180} />
            ))}
          </div>
        ) : profile.isError ? (
          <ErrorState
            title="Could not load your settings"
            body={userFacingMessage(profile.error)}
            action={<Button onClick={() => void profile.refetch()}>Try again</Button>}
          />
        ) : (
          <>
            <p className={styles.lead}>Manage your account, plan and data. Pick a section to open it.</p>

            {/* Four groups, as in the redesign, in the order that reads outward:
                who you are, how your calls are handled, what you pay for, and
                only then the two operations that remove things.

                Keyed by the saved values so a card rebuilds its draft when the
                server returns something different from what was typed. */}
            <OpenSectionsProvider>
            <div className={styles.page}>
              <h2 className={styles.group}>Account</h2>
              <PersonalCard key={`personal-${profile.dataUpdatedAt}`} profile={profile.data} />
              <CompanyCard key={`company-${profile.dataUpdatedAt}`} profile={profile.data} />

              <h2 className={styles.group}>How YourDealPulse works</h2>
              <CallsCard key={`calls-${profile.dataUpdatedAt}`} profile={profile.data} />
              <AutoArchiveCard key={`archive-${profile.dataUpdatedAt}`} profile={profile.data} />

              <h2 className={styles.group}>Plan &amp; billing</h2>
              <PlanCard />
              <BillingDetailsCard key={`billing-${profile.dataUpdatedAt}`} profile={profile.data} />

              <h2 className={styles.group}>Your data</h2>
              <ExportCard />
              <DeleteAccountCard />
            </div>
            </OpenSectionsProvider>
          </>
        )}
      </main>
    </>
  )
}
